import { useQuery } from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import {
  InputSelectControl,
  Primary13Regular,
  RedRed60,
  Box
} from '@sequencehq/core-components'
import { ValidationError } from '@sequencehq/utils'
import { uniqBy } from 'lodash/fp'
import { ReactNode, useEffect, useMemo, useState } from 'react'

type Product = Dashboardv99990101Api.GetProducts.Product
export const ProductSelect = (props: {
  productId: string
  disabledProducts?: string[]
  onChange: (newProductId: string) => void
  disabled?: boolean
  styles?: React.CSSProperties
  trailingIcon?: ReactNode
  isInvalid?: boolean
  validationErrors?: ValidationError[]
  'data-testid'?: string
}) => {
  const [productSearch, setProductSearch] = useState('')
  const [allProducts, setAllProducts] = useState<Product[]>([])

  const productsQuery = useQuery(
    dashboardv99990101Client.getProducts,
    {
      limit: 100,
      name: productSearch ? productSearch : undefined
    },
    {
      select: data => {
        if (!data) return []
        return data.items ?? []
      }
    }
  )

  /**
   * As we search, build up the list of products. This not only speeds things
   * up for the user, but also ensures we don't run into any issues where a search
   * will leave us without the originally selected options being available.
   */
  useEffect(() => {
    setAllProducts(prev =>
      uniqBy<Product>('id')([...prev, ...(productsQuery.data ?? [])])
    )
  }, [productsQuery.data])

  const productOptions = useMemo(() => {
    return (
      allProducts.map(product => ({
        label: product.name,
        value: product.id,
        disabled: props.disabledProducts?.includes(product.id)
      })) ?? []
    )
  }, [allProducts, props.disabledProducts])

  const hasErrors = props.validationErrors && props.validationErrors.length > 0

  return (
    <>
      <InputSelectControl
        data-testid={props['data-testid']}
        filterable
        disabled={props.disabled}
        triggerStyle={props.styles}
        onSearch={setProductSearch}
        options={productOptions}
        onChange={props.onChange}
        value={props.productId}
        placeholder="Select product"
        trailingIcon={props.trailingIcon}
        isInvalid={props.isInvalid || hasErrors}
      />
      {hasErrors && (
        <Box mt="8px" {...Primary13Regular} color={RedRed60}>
          {props.validationErrors![0].message}
        </Box>
      )}
    </>
  )
}
